import NiceModal from "@ebay/nice-modal-react";
import { useNavigate } from "react-router-dom";

import SimpleModal from "components/modals/SimpleModal";
import { Notice, TErrCodes } from "types/types";
import {
  useFetchData,
  useFetchPaginatedData,
  useGenericMutation,
} from "../useAxios";

export const useGetNotices = ({
  page,
  size,
  type,
  startDate,
  endDate,
  category,
  searchType,
  searchText,
  sort,
}: {
  page: number;
  size: number;
  type: "notice" | "faq";
  startDate?: string;
  endDate?: string;
  category?: string;
  searchType?: string;
  searchText?: string;
  sort?: string;
}) =>
  useFetchPaginatedData<Notice>({
    endpoint: "v1/notices",
    params: {
      page,
      size,
      type,
      startDate,
      endDate,
      category,
      searchType,
      searchText,
      sort,
    },
    queryKey: [
      "notices",
      type,
      startDate ?? "",
      endDate ?? "",
      category ?? "all",
      searchType ?? "",
      searchText ?? "",
      sort ?? "",
      page,
      size,
    ],
    keepPreviousData: true,
    staleTime: 60 * 60 * 60,
  });

export const useGetNotice = ({
  id,
  type,
}: {
  id?: number;
  type: "notice" | "faq";
}) =>
  useFetchData<Notice>({
    params: { type },
    endpoint: `v1/notices/${id}`,
    queryKey: ["notice", id ?? ""],
    invalidateQueries: [["notices", type === "notice" ? "notice" : "faq"]],
    enabled: !!id,
    showLoading: true,
  });

export const useNewNotice = () => {
  const navigate = useNavigate();

  return useGenericMutation<{
    title: string;
    content: string;
    type: string;
    author: string;
    isFixed?: number;
  }>({
    endpoint: "v1/notices/",
    invalidateQueries: (_, { type }) => [
      ["notices", type === "공지" ? "notice" : "faq"],
    ],
    onSuccess: (_, { type }) => {
      NiceModal.show(SimpleModal, {
        title: `${type === "공지" ? "공지" : "FAQ"} 등록`,
        message: "등록 완료되었습니다.",
        showCancel: false,
      }).then(() => {
        navigate(-1);
      });
    },
  });
};

export const useUpdateNotice = () => {
  const navigate = useNavigate();

  return useGenericMutation<{
    id: number;
    title: string;
    content: string;
    type: string;
    author: string;
    isFixed?: number;
  }>({
    method: "put",
    endpoint: ({ id }) => `v1/notices/${id}`,
    invalidateQueries: (_, { id, type }) => [
      ["notice", id],
      ["notices", type === "공지" ? "notice" : "faq"],
    ],
    onSuccess: (_, { type }) => {
      NiceModal.show(SimpleModal, {
        title: `${type === "공지" ? "공지" : "FAQ"} 수정`,
        message: "수정 완료되었습니다.",
        showCancel: false,
      }).then(() => {
        navigate(-1);
      });
    },
  });
};

export const useDeleteNotices = () =>
  useGenericMutation<{ ids: number[]; type: "faq" | "notice" }>({
    endpoint: () => `v1/notices/delete`,
    invalidateQueries: (_, { type }) => [["notices", type]],
    onSuccess: (_, { type }) => {
      NiceModal.show(SimpleModal, {
        title: `${type === "notice" ? "공지" : "FAQ"} 삭제`,
        message: "삭제 완료되었습니다.",
        showCancel: false,
      });
    },
  });

export const useHasUnread = () =>
  useGenericMutation<Record<string, void>, { hasUnread: boolean }>({
    endpoint: () => `v1/notices/unread`,
    showLoading: false,
    meta: {
      errCode: TErrCodes.NONE,
    },
  });
